import { useState } from 'preact/hooks';
import { post } from '../api';
import { Button, Card, Field, toast, useAction } from '../ui';
import { pathOf, type Location, type LocationMap } from './LocationsView';

/** Mirrors Cast in @saga/spec. */
export type CastTier = 'main' | 'secondary' | 'peripheral';
export interface CastMember {
	id: string;
	name: string;
	aliases: string[];
	tier: CastTier;
	role: string;
	description: string;
	inventory: Item[];
	appearsIn: string[];
}
export interface Item {
	id: string;
	name: string;
	description: string;
	quantity: number;
}
export interface Cast {
	characters: CastMember[];
}
export interface CastIssue {
	severity: 'error' | 'warning';
	message: string;
	id?: string;
}

/** Mirrors StoryData in @saga/spec: the whole setup the characters page edits. */
export interface Moment {
	id: string;
	label: string;
	placements: Array<{ character: string; location: string; inferred: boolean; conditions: MoveCondition[] }>;
}
/** What must be true before a character can make a move: carry an item, have someone with them, or an event. */
export interface MoveCondition {
	id: string;
	kind: 'item' | 'with' | 'event';
	description: string;
	item: string | null;
	with: string | null;
}

/** Mirrors movers() in @saga/spec: who changes place going into moment `index`, from where to where. */
export function movers(steps: Moment[], index: number): Array<{ character: string; from: string | null; to: string }> {
	const step = steps[index];
	if (!step) return [];
	const before = new Map<string, string>();
	for (const s of steps.slice(0, index)) for (const p of s.placements) before.set(p.character, p.location);
	return step.placements.filter((p) => !p.inferred && before.get(p.character) !== p.location).map((p) => ({ character: p.character, from: before.get(p.character) ?? null, to: p.location }));
}
export interface Setup {
	title: string;
	characters: CastMember[];
	locations: Location[];
	characterLocations: Moment[];
}

/** Mirrors StoryEdit in @saga/spec (the server checks every edit). */
export type StoryEdit = { op: string } & Record<string, unknown>;
export interface EditResult {
	section: 'characters' | 'locations' | 'characterLocations';
	message: string;
	ok: boolean;
	id?: string;
}

export const TIERS: Array<{ tier: CastTier; label: string; hint: string; tone: 'accent' | 'ok' | 'muted' }> = [
	{ tier: 'main', label: 'Main characters', hint: 'Primary: the story is about them, and players can play them.', tone: 'accent' },
	{ tier: 'secondary', label: 'Secondary characters', hint: 'They matter to the plot in some scenes.', tone: 'ok' },
	{ tier: 'peripheral', label: 'Peripheral characters', hint: 'Minor or background parts.', tone: 'muted' },
];

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Send a batch of edits; the server applies all or none and returns what each did. */
export async function applyEdits(storyId: string, edits: StoryEdit[]): Promise<EditResult[]> {
	const r = await post(`/api/stories/${storyId}/edits`, { edits });
	return r.results as EditResult[];
}

/**
 * The story's setup, centred on its characters: the cast by tier (drag a card to another tier to
 * move it), the locations, and the timeline of who is where, all editable. Every change goes
 * through the server's edit checks.
 */
export function CastView(p: { storyId: string; data: Setup; issues: CastIssue[]; locked: boolean; focus?: string | null; reload: () => void; openLocation?: (id: string) => void; openTimeline?: (id: string) => void }) {
	const chars = p.data.characters;
	const map: LocationMap = { title: p.data.title, locations: p.data.locations };
	const [sel, setSel] = useState<string | null>((p.focus && chars.some((c) => c.id === p.focus) ? p.focus : null) ?? chars.find((c) => c.tier === 'main')?.id ?? chars[0]?.id ?? null);
	const [adding, setAdding] = useState<CastTier | null>(null);
	const [dragOver, setDragOver] = useState<CastTier | null>(null);
	const c = chars.find((x) => x.id === sel) ?? null;
	const [run, busy] = useAction(async (edits: StoryEdit[], then?: (r: EditResult[]) => void) => {
		const r = await applyEdits(p.storyId, edits);
		toast(r.map((x) => x.message).join(' '), 'ok');
		then?.(r);
		p.reload();
	});
	const edit = (edits: StoryEdit[], then?: (r: EditResult[]) => void) => (p.locked ? toast('Wait for the compile to finish first.', 'warn') : run(edits, then));
	const drop = (tier: CastTier, e: DragEvent) => {
		e.preventDefault();
		setDragOver(null);
		const id = e.dataTransfer?.getData('text/x-character');
		const who = chars.find((x) => x.id === id);
		if (who && who.tier !== tier) edit([{ op: 'update_character', id: who.id, tier }]);
	};
	return (
		<div class="two-col wide-left">
			<div>
				{p.locked ? <p class="callout small">Compiling: editing opens when the compile finishes.</p> : null}
				{TIERS.map((t) => {
					const group = chars.filter((x) => x.tier === t.tier);
					return (
						<div key={t.tier} class={`tier-drop ${dragOver === t.tier ? 'over' : ''}`} onDragOver={(e) => (e.preventDefault(), setDragOver(t.tier))} onDragLeave={() => setDragOver(null)} onDrop={(e) => drop(t.tier, e)}>
							<Card
								title={`${t.label} (${group.length})`}
								actions={
									<Button small variant="ghost" disabled={p.locked} onClick={() => setAdding(adding === t.tier ? null : t.tier)}>
										{adding === t.tier ? 'Cancel' : '+ Add'}
									</Button>
								}
							>
								<p class="small muted">{t.hint}</p>
								{adding === t.tier ? <AddCharacter tier={t.tier} busy={busy} add={(e) => edit([e], (r) => (setAdding(null), setSel(r[0]?.id ?? null)))} /> : null}
								{group.length ? (
									<div class="cast-grid">
										{group.map((x) => (
											<button
												key={x.id}
												class={`cast-card tier-${x.tier} ${sel === x.id ? 'sel' : ''}`}
												draggable={!p.locked}
												onDragStart={(e) => e.dataTransfer?.setData('text/x-character', x.id)}
												onClick={() => setSel(x.id)}
												aria-pressed={sel === x.id}
												title="Drag to another tier to move"
											>
												<strong>{x.name}</strong>
												{x.role ? <span class="small muted">{x.role}</span> : null}
												{x.description ? <span class="small">{clip(x.description, 110)}</span> : null}
											</button>
										))}
									</div>
								) : (
									<p class="small muted">None. Drag a character here to move them to this tier.</p>
								)}
							</Card>
						</div>
					);
				})}
				<LocationsEditor data={p.data} map={map} busy={busy} locked={p.locked} edit={edit} openLocation={p.openLocation} />
				<TimelineEditor data={p.data} map={map} busy={busy} locked={p.locked} edit={edit} select={setSel} />
			</div>
			<div>
				{c ? <CharacterEditor key={c.id + JSON.stringify(c)} c={c} data={p.data} map={map} busy={busy} locked={p.locked} edit={edit} deselect={() => setSel(null)} openLocation={p.openLocation} openTimeline={p.openTimeline} /> : null}
				{p.issues.length ? (
					<Card title="To check">
						<ul class="issues">
							{p.issues.map((i, n) => (
								<li key={n} class={i.severity === 'error' ? 'bad' : 'warn'}>
									{i.id ? (
										<button class="linkish" onClick={() => setSel(i.id!)}>
											{i.message}
										</button>
									) : (
										i.message
									)}
								</li>
							))}
						</ul>
					</Card>
				) : null}
			</div>
		</div>
	);
}

type Edit = (edits: StoryEdit[], then?: (r: EditResult[]) => void) => void;

/** A location's name with the places it is inside ("Town › The Inn"). */
export function placeName(map: LocationMap, id: string): string {
	return pathOf(map, id).map((l) => l.name).reverse().join(' › ') || id;
}

/** Locations in nested order (each followed by what is inside it), with their depth. */
function nested(map: LocationMap): Array<{ l: Location; depth: number }> {
	const out: Array<{ l: Location; depth: number }> = [];
	const ids = new Set(map.locations.map((l) => l.id));
	const walk = (parent: string | null, depth: number) => {
		for (const l of map.locations.filter((x) => (parent ? x.parent === parent : !x.parent || !ids.has(x.parent)))) {
			if (out.some((o) => o.l === l)) continue;
			out.push({ l, depth });
			walk(l.id, depth + 1);
		}
	};
	walk(null, 0);
	return out;
}

function PlaceSelect(p: { map: LocationMap; value: string; onChange: (v: string) => void; empty?: string; label?: string }) {
	return (
		<select value={p.value} onChange={(e) => p.onChange(e.currentTarget.value)} aria-label={p.label}>
			{p.empty !== undefined ? <option value="">{p.empty}</option> : null}
			{nested(p.map).map(({ l, depth }) => (
				<option key={l.id} value={l.id}>
					{'  '.repeat(depth)}
					{l.name}
				</option>
			))}
		</select>
	);
}

function AddCharacter(p: { tier: CastTier; busy: boolean; add: (e: StoryEdit) => void }) {
	const [name, setName] = useState('');
	const [role, setRole] = useState('');
	return (
		<form
			class="inline-form"
			onSubmit={(e) => {
				e.preventDefault();
				if (name.trim()) p.add({ op: 'add_character', name: name.trim(), tier: p.tier, role: role.trim() });
			}}
		>
			<input value={name} onInput={(e) => setName(e.currentTarget.value)} placeholder="Name" aria-label="New character name" maxLength={80} autoFocus />
			<input value={role} onInput={(e) => setRole(e.currentTarget.value)} placeholder="Role (optional)" aria-label="New character role" maxLength={80} />
			<Button small type="submit" busy={p.busy} disabled={!name.trim()}>
				Add
			</Button>
		</form>
	);
}

/** The selected character: edit their details or tier, delete them, and place them on the timeline. */
function CharacterEditor(p: { c: CastMember; data: Setup; map: LocationMap; busy: boolean; locked: boolean; edit: Edit; deselect: () => void; openLocation?: (id: string) => void; openTimeline?: (id: string) => void }) {
	const c = p.c;
	const [name, setName] = useState(c.name);
	const [tier, setTier] = useState<CastTier>(c.tier);
	const [role, setRole] = useState(c.role);
	const [aliases, setAliases] = useState(c.aliases.join(', '));
	const [description, setDescription] = useState(c.description);
	const list = aliases.split(',').map((a) => a.trim()).filter(Boolean);
	const dirty = name !== c.name || tier !== c.tier || role !== c.role || description !== c.description || list.join('|') !== c.aliases.join('|');
	const save = () => p.edit([{ op: 'update_character', id: c.id, name: name.trim(), tier, role, description, aliases: list }]);
	const remove = () => confirm(`Delete ${c.name}? They are also taken off the timeline.`) && p.edit([{ op: 'delete_character', id: c.id }], p.deselect);
	const placed = p.data.characterLocations.flatMap((s, i) => s.placements.filter((x) => x.character === c.id).map((x) => ({ s, i, x })));
	return (
		<Card title={c.name} actions={<code class="small">{c.id}</code>}>
			<Field label="Name">
				<input value={name} onInput={(e) => setName(e.currentTarget.value)} maxLength={80} />
			</Field>
			<Field label="Tier">
				<select value={tier} onChange={(e) => setTier(e.currentTarget.value as CastTier)} aria-label="Tier">
					{TIERS.map((t) => (
						<option key={t.tier} value={t.tier}>
							{t.label.replace(' characters', '')}
							{t.tier === 'main' ? ' (primary, playable)' : ''}
						</option>
					))}
				</select>
			</Field>
			<Field label="Role">
				<input value={role} onInput={(e) => setRole(e.currentTarget.value)} maxLength={80} />
			</Field>
			<Field label="Also called" hint="Separate names with commas.">
				<input value={aliases} onInput={(e) => setAliases(e.currentTarget.value)} />
			</Field>
			<Field label="Description">
				<textarea rows={4} value={description} onInput={(e) => setDescription(e.currentTarget.value)} maxLength={600} />
			</Field>
			<div class="row">
				<Button small onClick={save} busy={p.busy} disabled={!dirty || !name.trim() || p.locked}>
					Save changes
				</Button>
				<Button small variant="danger" onClick={remove} disabled={p.locked}>
					Delete character
				</Button>
			</div>
			<InventoryEditor c={c} busy={p.busy} locked={p.locked} edit={p.edit} />
			<h3>On the timeline</h3>
			{placed.length ? (
				<ul class="list small">
					{placed.map(({ s, i, x }) => (
						<li key={s.id}>
							<div class="row-between">
							<span>
								<span class="muted">{i + 1}.</span> {s.label || s.id}:{' '}
								{p.openLocation ? (
									<button class="linkish" onClick={() => p.openLocation!(x.location)}>
										{placeName(p.map, x.location)}
									</button>
								) : (
									placeName(p.map, x.location)
								)}
								{x.inferred ? <span class="muted"> (carried over)</span> : null}
							</span>
							<button class="linkish danger" disabled={p.locked} onClick={() => p.edit([{ op: 'unplace_character', moment: s.id, character: c.id }])} aria-label={`Take ${c.name} out of ${s.label || s.id}`}>
								remove
							</button>
							</div>
							{movers(p.data.characterLocations, i).find((m) => m.character === c.id)?.from ? <MoveConditions moment={s.id} c={c} conditions={x.conditions} data={p.data} busy={p.busy} locked={p.locked} edit={p.edit} /> : null}
						</li>
					))}
				</ul>
			) : (
				<p class="small muted">Not placed anywhere yet.</p>
			)}
			{p.data.locations.length ? <PlaceOnTimeline c={c} data={p.data} map={p.map} busy={p.busy} locked={p.locked} edit={p.edit} /> : <p class="small muted">Add a location below to place {c.name} on the timeline.</p>}
			{p.openTimeline && placed.length ? (
				<button class="linkish small" onClick={() => p.openTimeline!(c.id)}>
					Follow {c.name} on the timeline
				</button>
			) : null}
		</Card>
	);
}

/** Put a character (and anyone with them) at a location, in a new moment or an existing one. */
function PlaceOnTimeline(p: { c: CastMember; data: Setup; map: LocationMap; busy: boolean; locked: boolean; edit: Edit }) {
	const [location, setLocation] = useState('');
	const [moment, setMoment] = useState('');
	const [label, setLabel] = useState('');
	const [with_, setWith] = useState<string[]>([]);
	const others = p.data.characters.filter((x) => x.id !== p.c.id);
	const go = () => {
		const who = [p.c.id, ...with_];
		if (moment) p.edit(who.map((character) => ({ op: 'place_character', moment, character, location })));
		else p.edit([{ op: 'add_moment', label: label.trim() || `${p.c.name} at ${placeName(p.map, location)}`, placements: who.map((character) => ({ character, location })) }]);
	};
	return (
		<details class="place-form">
			<summary class="small">Add {p.c.name} to the timeline…</summary>
			<Field label="At">
				<PlaceSelect map={p.map} value={location} onChange={setLocation} empty="Choose a location…" label="Location" />
			</Field>
			<Field label="When">
				<select value={moment} onChange={(e) => setMoment(e.currentTarget.value)} aria-label="Moment">
					<option value="">A new moment at the end</option>
					{p.data.characterLocations.map((s, i) => (
						<option key={s.id} value={s.id}>
							{i + 1}. {clip(s.label || s.id, 60)}
						</option>
					))}
				</select>
			</Field>
			{!moment ? (
				<Field label="What happens">
					<input value={label} onInput={(e) => setLabel(e.currentTarget.value)} placeholder="e.g. Mira arrives at the inn" maxLength={200} />
				</Field>
			) : null}
			{others.length ? (
				<fieldset class="with-list">
					<legend class="small">With</legend>
					{others.map((o) => (
						<label key={o.id} class="small">
							<input type="checkbox" checked={with_.includes(o.id)} onChange={(e) => setWith(e.currentTarget.checked ? [...with_, o.id] : with_.filter((x) => x !== o.id))} /> {o.name}
						</label>
					))}
				</fieldset>
			) : null}
			<Button small onClick={go} busy={p.busy} disabled={!location || p.locked}>
				Place on the timeline
			</Button>
		</details>
	);
}

/** The story's places, nested, with add and delete. */
function LocationsEditor(p: { data: Setup; map: LocationMap; busy: boolean; locked: boolean; edit: Edit; openLocation?: (id: string) => void }) {
	const [name, setName] = useState('');
	const [parent, setParent] = useState('');
	const uses = (id: string) => p.data.characterLocations.filter((s) => s.placements.some((x) => x.location === id)).length;
	const add = () => p.edit([{ op: 'add_location', name: name.trim(), parent: parent || null }], () => setName(''));
	return (
		<Card title={`Locations (${p.data.locations.length})`}>
			{p.data.locations.length ? (
				<ul class="list small">
					{nested(p.map).map(({ l, depth }) => {
						const n = uses(l.id);
						return (
							<li key={l.id} class="row-between" style={{ paddingLeft: `${depth * 18}px` }}>
								<span>
									{p.openLocation ? (
										<button class="linkish" onClick={() => p.openLocation!(l.id)}>
											{l.name}
										</button>
									) : (
										l.name
									)}
									{n ? <span class="muted"> · {n} moment{n === 1 ? '' : 's'}</span> : null}
								</span>
								<button
									class="linkish danger"
									disabled={p.locked || n > 0}
									title={n ? 'Used on the timeline: move those characters first.' : undefined}
									onClick={() => confirm(`Delete ${l.name}? Places inside it move up a level.`) && p.edit([{ op: 'delete_location', id: l.id }])}
									aria-label={`Delete ${l.name}`}
								>
									delete
								</button>
							</li>
						);
					})}
				</ul>
			) : (
				<p class="small muted">No locations yet.</p>
			)}
			<form
				class="inline-form"
				onSubmit={(e) => {
					e.preventDefault();
					if (name.trim()) add();
				}}
			>
				<input value={name} onInput={(e) => setName(e.currentTarget.value)} placeholder="New location" aria-label="New location name" maxLength={80} />
				<PlaceSelect map={p.map} value={parent} onChange={setParent} empty="Top level" label="Inside" />
				<Button small type="submit" busy={p.busy} disabled={!name.trim() || p.locked}>
					Add location
				</Button>
			</form>
		</Card>
	);
}

/** The moments in order: who is where, with add, remove and delete. */
function TimelineEditor(p: { data: Setup; map: LocationMap; busy: boolean; locked: boolean; edit: Edit; select: (id: string) => void }) {
	const [label, setLabel] = useState('');
	const [location, setLocation] = useState('');
	const [who, setWho] = useState<string[]>([]);
	const [after, setAfter] = useState('end');
	const nameOf = (id: string) => p.data.characters.find((c) => c.id === id)?.name ?? id;
	const add = () =>
		p.edit([{ op: 'add_moment', label: label.trim(), placements: who.map((character) => ({ character, location })), ...(after === 'end' ? {} : { after: after === 'start' ? null : after }) }], () => (setLabel(''), setWho([])));
	return (
		<Card title={`Timeline (${p.data.characterLocations.length} moments)`}>
			{p.data.characterLocations.length ? (
				<ol class="moments small">
					{p.data.characterLocations.map((s, i) => (
						<li key={s.id}>
							<div class="row-between">
								<strong>{s.label || s.id}</strong>
								<button class="linkish danger" disabled={p.locked} onClick={() => confirm(`Delete the moment "${s.label || s.id}"?`) && p.edit([{ op: 'delete_moment', id: s.id }])} aria-label={`Delete moment ${s.label || s.id}`}>
									delete
								</button>
							</div>
							<div class="row wrap">
								{s.placements.map((x) => (
									<span key={x.character} class={`placement ${x.inferred ? 'inferred' : ''}`}>
										<button class="linkish" onClick={() => p.select(x.character)}>
											{nameOf(x.character)}
										</button>{' '}
										@ {placeName(p.map, x.location)}
										<button class="x" disabled={p.locked} onClick={() => p.edit([{ op: 'unplace_character', moment: s.id, character: x.character }])} aria-label={`Remove ${nameOf(x.character)} from this moment`}>
											×
										</button>
									</span>
								))}
								{s.placements.length ? null : <span class="muted">Nobody.</span>}
							</div>
							{i > 0 ? <Transition steps={p.data.characterLocations} index={i} data={p.data} map={p.map} busy={p.busy} locked={p.locked} edit={p.edit} nameOf={nameOf} /> : null}
						</li>
					))}
				</ol>
			) : (
				<p class="small muted">No moments yet.</p>
			)}
			{p.data.locations.length && p.data.characters.length ? (
				<details class="place-form">
					<summary class="small">Add a moment…</summary>
					<Field label="What happens">
						<input value={label} onInput={(e) => setLabel(e.currentTarget.value)} placeholder="e.g. The party reaches the crypt" maxLength={200} />
					</Field>
					<Field label="Where">
						<PlaceSelect map={p.map} value={location} onChange={setLocation} empty="Choose a location…" label="Moment location" />
					</Field>
					<fieldset class="with-list">
						<legend class="small">Who is there</legend>
						{p.data.characters.map((o) => (
							<label key={o.id} class="small">
								<input type="checkbox" checked={who.includes(o.id)} onChange={(e) => setWho(e.currentTarget.checked ? [...who, o.id] : who.filter((x) => x !== o.id))} /> {o.name}
							</label>
						))}
					</fieldset>
					<Field label="Position">
						<select value={after} onChange={(e) => setAfter(e.currentTarget.value)} aria-label="Position">
							<option value="end">At the end</option>
							<option value="start">At the start</option>
							{p.data.characterLocations.map((s, i) => (
								<option key={s.id} value={s.id}>
									After {i + 1}. {clip(s.label || s.id, 50)}
								</option>
							))}
						</select>
					</Field>
					<Button small onClick={add} busy={p.busy} disabled={!location || !who.length || p.locked}>
						Add moment
					</Button>
				</details>
			) : (
				<p class="small muted">Add characters and locations to build the timeline.</p>
			)}
		</Card>
	);
}

/** A character's inventory: change, remove and add items. */
function InventoryEditor(p: { c: CastMember; busy: boolean; locked: boolean; edit: Edit }) {
	const c = p.c;
	const [editing, setEditing] = useState<string | null>(null);
	return (
		<>
			<h3>Inventory</h3>
			{c.inventory.length ? (
				<ul class="list small inventory">
					{c.inventory.map((i) =>
						editing === i.id ? (
							<li key={i.id}>
								<ItemForm item={i} busy={p.busy} locked={p.locked} label="Save item" cancel={() => setEditing(null)} save={(v) => p.edit([{ op: 'update_item', character: c.id, id: i.id, ...v }], () => setEditing(null))} />
							</li>
						) : (
							<li key={i.id} class="row-between">
								<span>
									<strong>{i.name}</strong>
									{i.quantity > 1 ? <span class="muted"> × {i.quantity}</span> : null}
									{i.description ? <span class="muted"> · {i.description}</span> : null}
								</span>
								<span class="row">
									<button class="linkish" disabled={p.locked} onClick={() => setEditing(i.id)} aria-label={`Edit ${i.name}`}>
										edit
									</button>
									<button class="linkish danger" disabled={p.locked} onClick={() => p.edit([{ op: 'remove_item', character: c.id, id: i.id }])} aria-label={`Remove ${i.name}`}>
										remove
									</button>
								</span>
							</li>
						),
					)}
				</ul>
			) : (
				<p class="small muted">{c.name} carries nothing yet.</p>
			)}
			<details class="place-form">
				<summary class="small">Give {c.name} an item…</summary>
				<ItemForm busy={p.busy} locked={p.locked} label="Add item" save={(v) => p.edit([{ op: 'add_item', character: c.id, ...v }])} />
			</details>
		</>
	);
}

function ItemForm(p: { item?: Item; busy: boolean; locked: boolean; label: string; save: (v: { name: string; quantity: number; description: string }) => void; cancel?: () => void }) {
	const [name, setName] = useState(p.item?.name ?? '');
	const [quantity, setQuantity] = useState(p.item?.quantity ?? 1);
	const [description, setDescription] = useState(p.item?.description ?? '');
	return (
		<form
			class="inline-form"
			onSubmit={(e) => {
				e.preventDefault();
				if (!name.trim()) return;
				p.save({ name: name.trim(), quantity: Math.max(1, Math.round(quantity) || 1), description: description.trim() });
				if (!p.item) (setName(''), setQuantity(1), setDescription(''));
			}}
		>
			<input value={name} onInput={(e) => setName(e.currentTarget.value)} placeholder="Item" aria-label="Item name" maxLength={80} />
			<input class="qty" type="number" min={1} max={9999} value={quantity} onInput={(e) => setQuantity(Number(e.currentTarget.value))} aria-label="Quantity" />
			<input value={description} onInput={(e) => setDescription(e.currentTarget.value)} placeholder="Description (optional)" aria-label="Item description" maxLength={300} />
			<Button small type="submit" busy={p.busy} disabled={!name.trim() || p.locked}>
				{p.label}
			</Button>
			{p.cancel ? (
				<Button small variant="ghost" type="button" onClick={p.cancel}>
					Cancel
				</Button>
			) : null}
		</form>
	);
}

/** The moves into a moment: who goes where, and what each of them must meet first. */
function Transition(p: { steps: Moment[]; index: number; data: Setup; map: LocationMap; busy: boolean; locked: boolean; edit: Edit; nameOf: (id: string) => string }) {
	const s = p.steps[p.index];
	const moving = movers(p.steps, p.index);
	const where = (id: string) => p.map.locations.find((l) => l.id === id)?.name ?? id;
	if (!moving.length) return null;
	return (
		<div class="transition">
			{moving.map((m) => {
				const c = p.data.characters.find((x) => x.id === m.character);
				const placement = s.placements.find((x) => x.character === m.character);
				return (
					<div key={m.character} class="move">
						<p class="small">
							<strong>{p.nameOf(m.character)}</strong> <span class="muted">{m.from ? `${where(m.from)} → ${where(m.to)}` : `enters at ${where(m.to)}`}</span>
						</p>
						{m.from && c && placement ? <MoveConditions moment={s.id} c={c} conditions={placement.conditions} data={p.data} busy={p.busy} locked={p.locked} edit={p.edit} /> : null}
					</div>
				);
			})}
		</div>
	);
}

const KIND_LABEL: Record<MoveCondition['kind'], string> = { item: 'carries', with: 'with', event: 'event' };

/** What must be true before one character makes one move: list, change, remove and add. */
function MoveConditions(p: { moment: string; c: CastMember; conditions: MoveCondition[]; data: Setup; busy: boolean; locked: boolean; edit: Edit }) {
	const [editing, setEditing] = useState<string | null>(null);
	const nameOf = (id: string | null) => p.data.characters.find((x) => x.id === id)?.name ?? id ?? '';
	const target = { moment: p.moment, character: p.c.id };
	return (
		<div class="conditions">
			{p.conditions.length ? (
				<ul class="condition-list">
					{p.conditions.map((x) =>
						editing === x.id ? (
							<li key={x.id}>
								<ConditionForm cond={x} c={p.c} data={p.data} busy={p.busy} locked={p.locked} label="Save" cancel={() => setEditing(null)} save={(v) => p.edit([{ op: 'update_condition', ...target, id: x.id, ...v }], () => setEditing(null))} />
							</li>
						) : (
							<li key={x.id} class="row-between small">
								<span>
									<span class={`kind kind-${x.kind}`}>{KIND_LABEL[x.kind]}</span> {x.description}
									{x.kind === 'item' && x.item ? <span class="muted"> ({x.item})</span> : null}
									{x.kind === 'with' && x.with ? <span class="muted"> ({nameOf(x.with)})</span> : null}
								</span>
								<span class="row">
									<button class="linkish" disabled={p.locked} onClick={() => setEditing(x.id)} aria-label={`Edit condition ${x.description}`}>
										edit
									</button>
									<button class="linkish danger" disabled={p.locked} onClick={() => p.edit([{ op: 'remove_condition', ...target, id: x.id }])} aria-label={`Remove condition ${x.description}`}>
										remove
									</button>
								</span>
							</li>
						),
					)}
				</ul>
			) : (
				<p class="small muted">No conditions: {p.c.name} can make this move freely.</p>
			)}
			<details class="place-form">
				<summary class="small">Add a condition for {p.c.name}…</summary>
				<ConditionForm c={p.c} data={p.data} busy={p.busy} locked={p.locked} label="Add condition" save={(v) => p.edit([{ op: 'add_condition', ...target, ...v }])} />
			</details>
		</div>
	);
}

function ConditionForm(p: { cond?: MoveCondition; c: CastMember; data: Setup; busy: boolean; locked: boolean; label: string; save: (v: { kind: MoveCondition['kind']; description: string; item?: string; with?: string }) => void; cancel?: () => void }) {
	const [kind, setKind] = useState<MoveCondition['kind']>(p.cond?.kind ?? 'event');
	const [description, setDescription] = useState(p.cond?.description ?? '');
	const [item, setItem] = useState(p.cond?.item ?? '');
	const [with_, setWith] = useState(p.cond?.with ?? '');
	const items = [...new Set(p.data.characters.flatMap((x) => x.inventory.map((i) => i.name)))];
	const ready = description.trim() && (kind !== 'item' || item.trim()) && (kind !== 'with' || with_);
	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
				if (!ready) return;
				p.save({ kind, description: description.trim(), ...(kind === 'item' ? { item: item.trim() } : {}), ...(kind === 'with' ? { with: with_ } : {}) });
				if (!p.cond) (setDescription(''), setItem(''), setWith(''));
			}}
		>
			<div class="inline-form">
				<select value={kind} onChange={(e) => setKind(e.currentTarget.value as MoveCondition['kind'])} aria-label="Condition kind">
					<option value="event">Something must happen</option>
					<option value="item">Must carry an item</option>
					<option value="with">Someone must be with them</option>
				</select>
				{kind === 'item' ? (
					<>
						<input list={`items-${p.c.id}`} value={item} onInput={(e) => setItem(e.currentTarget.value)} placeholder="Item" aria-label="Item needed" maxLength={80} />
						<datalist id={`items-${p.c.id}`}>
							{items.map((n) => (
								<option key={n} value={n} />
							))}
						</datalist>
					</>
				) : null}
				{kind === 'with' ? (
					<select value={with_} onChange={(e) => setWith(e.currentTarget.value)} aria-label="Who must be with them">
						<option value="">Who…</option>
						{p.data.characters
							.filter((x) => x.id !== p.c.id)
							.map((x) => (
								<option key={x.id} value={x.id}>
									{x.name}
								</option>
							))}
					</select>
				) : null}
			</div>
			<Field label="Condition">
				<input value={description} onInput={(e) => setDescription(e.currentTarget.value)} placeholder={kind === 'item' ? 'e.g. Luke has the lightsaber Ben gave him' : kind === 'with' ? 'e.g. Ben goes with him' : 'e.g. Luke agrees to go to Alderaan'} maxLength={300} aria-label="Condition" />
			</Field>
			<div class="row">
				<Button small type="submit" busy={p.busy} disabled={!ready || p.locked}>
					{p.label}
				</Button>
				{p.cancel ? (
					<Button small variant="ghost" type="button" onClick={p.cancel}>
						Cancel
					</Button>
				) : null}
			</div>
		</form>
	);
}
